import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { CloudArrowUp, DeviceMobile, Trash, ArrowClockwise, CheckCircle, QrCode, Hammer, X } from '@phosphor-icons/react';

const formatBytes = (b) => {
  if (!b) return '—';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(2)} MB`;
};

const formatDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

export default function AppDistributionPage() {
  const [releases, setReleases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [form, setForm] = useState({ version_name: '', version_code: '', release_notes: '' });
  const [file, setFile] = useState(null);
  const fileRef = useRef(null);
  const API_BASE = process.env.REACT_APP_BACKEND_URL;

  // Server rebuild state
  const [build, setBuild] = useState({ status: 'idle', logs_tail: '', error: null });
  const [rebuildNotes, setRebuildNotes] = useState('');
  const [showLogs, setShowLogs] = useState(false);
  const buildPoll = useRef(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/app-distribution');
      setReleases(data || []);
    } catch {} finally { setLoading(false); }
  }, []);

  const fetchBuildStatus = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/app-distribution/build-status');
      setBuild(data);
      // When transitioning to a terminal state, refresh release list once.
      if (data.status === 'success' || data.status === 'failed') {
        if (buildPoll.current) { clearInterval(buildPoll.current); buildPoll.current = null; }
        refresh();
      }
    } catch {}
  }, [refresh]);

  useEffect(() => { refresh(); fetchBuildStatus(); }, [refresh, fetchBuildStatus]);

  // Auto-start polling when status becomes "running" (also covers reload mid-build).
  useEffect(() => {
    if (build.status === 'running' && !buildPoll.current) {
      buildPoll.current = setInterval(fetchBuildStatus, 3000);
    }
    return () => {
      if (buildPoll.current && build.status !== 'running') {
        clearInterval(buildPoll.current);
        buildPoll.current = null;
      }
    };
  }, [build.status, fetchBuildStatus]);

  const triggerRebuild = async () => {
    if (!window.confirm('Start a fresh APK build on the server? This takes ~3-5 minutes.\n\nNote: this only works on the PREVIEW environment. On production, the APK ships through redeploy.')) return;
    try {
      const fd = new FormData();
      if (rebuildNotes) fd.append('release_notes', rebuildNotes);
      await api.post('/admin/app-distribution/rebuild', fd);
      setShowLogs(true);
      setRebuildNotes('');
      fetchBuildStatus();
      if (!buildPoll.current) buildPoll.current = setInterval(fetchBuildStatus, 3000);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to start build';
      alert(msg);
      // Surface server-side reason inside the logs panel so admin sees it after dismissing the alert.
      setBuild(b => ({ ...b, status: 'failed', error: msg, logs_tail: msg }));
      setShowLogs(true);
    }
  };

  const current = releases.find(r => r.is_current);
  const downloadUrl = current ? `${API_BASE}${current.stable_url}` : '';
  const qrUrl = downloadUrl
    ? `https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encodeURIComponent(downloadUrl)}`
    : '';

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { alert('Please choose an APK file first'); return; }
    setUploading(true);
    setProgress(0);
    const fd = new FormData();
    fd.append('file', file);
    if (form.version_name) fd.append('version_name', form.version_name);
    if (form.version_code) fd.append('version_code', String(form.version_code));
    if (form.release_notes) fd.append('release_notes', form.release_notes);
    try {
      await api.post('/admin/app-distribution/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (evt) => setProgress(evt.total ? Math.round((evt.loaded / evt.total) * 100) : 0),
      });
      setFile(null);
      setForm({ version_name: '', version_code: '', release_notes: '' });
      if (fileRef.current) fileRef.current.value = '';
      refresh();
    } catch (err) {
      alert(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const promote = async (id) => {
    if (!window.confirm('Roll back to this release as the current build?')) return;
    try { await api.put(`/admin/app-distribution/${id}/promote`); refresh(); }
    catch (err) { alert(err.response?.data?.detail || 'Promote failed'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this release? The on-disk APK will be removed.')) return;
    try { await api.delete(`/admin/app-distribution/${id}`); refresh(); }
    catch (err) { alert(err.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <div className="space-y-4" data-testid="app-distribution-page">
      <div>
        <h1 className="text-3xl md:text-4xl font-black tracking-tighter text-[#0A0A0A] flex items-center gap-2">
          <DeviceMobile size={28} weight="duotone" /> App Distribution
        </h1>
        <p className="text-[#666] text-xs mt-1">
          Upload signed Android APKs. The latest is auto-served at the stable URL <code className="bg-[#F3F3F3] px-1">/api/uploads/vehicle.apk</code> for v2.0+ upgrade prompts.
        </p>
      </div>

      {/* Current release card + QR */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white border border-[#E5E5E5] p-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h2 className="text-[10px] uppercase tracking-widest font-bold text-[#666]">Current Release</h2>
            {current && (
              <span className="text-[10px] uppercase tracking-widest font-bold bg-[#00C853]/10 text-[#00C853] px-2 py-1 rounded flex items-center gap-1">
                <CheckCircle size={12} weight="bold" /> Live
              </span>
            )}
          </div>
          {current ? (
            <div className="mt-3 space-y-2">
              <div className="flex items-baseline gap-2">
                <span className="text-3xl font-black text-[#002FA7]" data-testid="current-version-name">v{current.version_name}</span>
                <span className="text-sm text-[#666] font-mono">build #{current.version_code}</span>
              </div>
              <p className="text-xs text-[#666]">
                {formatBytes(current.file_size)} · uploaded {formatDate(current.uploaded_at)} · by {current.uploaded_by_name || 'admin'}
              </p>
              {current.release_notes && (
                <div className="bg-[#F7F7F7] border border-[#E5E5E5] p-2 text-xs whitespace-pre-wrap">{current.release_notes}</div>
              )}
              <div className="flex flex-wrap items-center gap-2 pt-2">
                <a
                  href={downloadUrl}
                  className="bg-[#002FA7] text-white text-xs uppercase tracking-widest font-bold px-3 py-2 rounded-full hover:bg-[#002FA7]/90"
                  download="vehicle.apk"
                  data-testid="download-current-apk"
                >
                  Download APK
                </a>
                <span className="text-xs text-[#666] font-mono break-all" data-testid="stable-url">{downloadUrl}</span>
              </div>
            </div>
          ) : (
            <p className="mt-3 text-sm text-[#666]">No APK uploaded yet. Use the form below to publish your first release.</p>
          )}
        </div>
        <div className="bg-white border border-[#E5E5E5] p-4 flex flex-col items-center justify-center">
          <h2 className="text-[10px] uppercase tracking-widest font-bold text-[#666] flex items-center gap-1 self-start"><QrCode size={12} /> Install QR</h2>
          {qrUrl ? (
            <>
              <img src={qrUrl} alt="QR code" className="w-40 h-40 mt-2" data-testid="apk-qr" />
              <p className="text-[10px] text-[#666] mt-2 text-center">Scan with phone camera to download</p>
            </>
          ) : (
            <p className="text-xs text-[#666] mt-6">Upload an APK to generate QR</p>
          )}
        </div>
      </div>

      {/* Server rebuild panel */}
      <div className="bg-white border border-[#E5E5E5] p-4 space-y-3" data-testid="rebuild-panel">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h2 className="text-[10px] uppercase tracking-widest font-bold text-[#666] flex items-center gap-1">
            <Hammer size={14} /> One-Click Server Rebuild
          </h2>
          {build.status === 'running' && (
            <span className="text-[10px] uppercase tracking-widest font-bold bg-[#FF8C00]/10 text-[#FF8C00] px-2 py-1 rounded animate-pulse" data-testid="build-status-badge">
              Building…
            </span>
          )}
          {build.status === 'success' && (
            <span className="text-[10px] uppercase tracking-widest font-bold bg-[#00C853]/10 text-[#00C853] px-2 py-1 rounded" data-testid="build-status-badge">
              Last build: success
            </span>
          )}
          {build.status === 'failed' && (
            <span className="text-[10px] uppercase tracking-widest font-bold bg-[#FF3333]/10 text-[#FF3333] px-2 py-1 rounded" data-testid="build-status-badge">
              Last build: failed
            </span>
          )}
        </div>
        <p className="text-xs text-[#666]">
          Builds a fresh debug APK from the live source via <code className="bg-[#F3F3F3] px-1">/app/build-apk.sh</code>.
          Takes ~3-5 min. The new APK auto-publishes here as the current release.
        </p>
        <div className="bg-[#FFF8E1] border border-[#FFD54F] text-[#8B6F00] text-xs p-2 rounded flex gap-2">
          <span className="font-bold">Note:</span>
          <span>
            This button only works on the <strong>preview</strong> environment (the build tools are installed there). On <strong>production</strong> (<code>emergent.carloo.in</code>), build the APK on preview first → click <strong>Deploy</strong> in Emergent → the production <code>/api/uploads/vehicle.apk</code> updates automatically.
          </span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-2">
            <Label className="text-[10px] uppercase tracking-widest font-bold text-[#666]">Release Notes (optional)</Label>
            <Input
              value={rebuildNotes}
              onChange={e => setRebuildNotes(e.target.value)}
              placeholder="What changed in this build"
              className="rounded text-sm"
              disabled={build.status === 'running'}
              data-testid="rebuild-notes-input"
            />
          </div>
          <div className="flex items-end gap-2">
            <Button
              type="button"
              onClick={triggerRebuild}
              disabled={build.status === 'running'}
              className="w-full bg-[#FF8C00] text-white hover:bg-[#cc7000] hover:text-white rounded-full disabled:bg-[#AAAAAA]"
              data-testid="rebuild-apk-btn"
            >
              <Hammer size={14} className="mr-1" />
              {build.status === 'running' ? 'Building…' : 'Rebuild APK'}
            </Button>
          </div>
        </div>
        {(build.status === 'running' || build.status === 'failed' || (build.status === 'success' && showLogs)) && (
          <div className="bg-[#0A0A0A] text-[#A8FFB0] font-mono text-[11px] p-3 rounded max-h-64 overflow-auto whitespace-pre-wrap" data-testid="build-logs">
            {build.logs_tail || 'Waiting for build output…'}
            {build.error && <div className="text-[#FF6666] mt-2">ERROR: {build.error}</div>}
          </div>
        )}
        {build.status !== 'idle' && (
          <button
            onClick={() => setShowLogs(s => !s)}
            className="text-xs text-[#002FA7] hover:underline flex items-center gap-1"
            data-testid="toggle-build-logs"
          >
            {showLogs ? <X size={12} /> : null}
            {showLogs ? 'Hide build logs' : 'Show build logs'}
          </button>
        )}
      </div>

      {/* Upload form */}
      <form onSubmit={submit} className="bg-white border border-[#E5E5E5] p-4 space-y-3" data-testid="upload-apk-form">
        <h2 className="text-[10px] uppercase tracking-widest font-bold text-[#666] flex items-center gap-1">
          <CloudArrowUp size={14} /> Upload New Release
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-3">
            <Label className="text-[10px] uppercase tracking-widest font-bold text-[#666]">APK File *</Label>
            <input
              ref={fileRef}
              type="file"
              accept=".apk"
              onChange={e => setFile(e.target.files?.[0] || null)}
              className="mt-1 block w-full text-sm text-[#0A0A0A] file:mr-3 file:py-2 file:px-3 file:rounded-full file:border-0 file:bg-[#002FA7] file:text-white file:text-xs file:uppercase file:tracking-widest file:font-bold hover:file:bg-[#002FA7]/90"
              required
              data-testid="apk-file-input"
            />
            <p className="text-[10px] text-[#666] mt-1">Version is auto-detected from the APK manifest. Manual fields below are optional fallbacks.</p>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest font-bold text-[#666]">Version Name (auto)</Label>
            <Input
              value={form.version_name}
              onChange={e => setForm({ ...form, version_name: e.target.value })}
              placeholder="e.g. 2.0"
              className="rounded text-sm"
              data-testid="version-name-input"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest font-bold text-[#666]">Version Code (auto)</Label>
            <Input
              type="number"
              value={form.version_code}
              onChange={e => setForm({ ...form, version_code: e.target.value })}
              placeholder="e.g. 20"
              className="rounded text-sm"
              data-testid="version-code-input"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest font-bold text-[#666]">Release Notes</Label>
            <Input
              value={form.release_notes}
              onChange={e => setForm({ ...form, release_notes: e.target.value })}
              placeholder="What's new"
              className="rounded text-sm"
              data-testid="release-notes-input"
            />
          </div>
        </div>
        {uploading && (
          <div className="w-full bg-[#F3F3F3] h-2 rounded-full overflow-hidden">
            <div className="h-full bg-[#002FA7] transition-all" style={{ width: `${progress}%` }} />
          </div>
        )}
        <Button
          type="submit"
          disabled={uploading || !file}
          className="bg-[#002FA7] text-white hover:bg-[#002FA7]/90 hover:text-white rounded-full disabled:bg-[#AAAAAA]"
          data-testid="upload-apk-submit"
        >
          {uploading ? `Uploading… ${progress}%` : 'Publish Release'}
        </Button>
      </form>

      {/* History */}
      <div className="bg-white border border-[#E5E5E5] overflow-x-auto">
        <h2 className="text-[10px] uppercase tracking-widest font-bold text-[#666] p-4 pb-2">Release History</h2>
        {loading ? (
          <p className="text-center py-8 text-sm text-[#666]">Loading…</p>
        ) : releases.length === 0 ? (
          <p className="text-center py-8 text-sm text-[#666]">No releases yet.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-[#F3F3F3]">
                <TableHead className="text-[10px] uppercase tracking-widest">Version</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest">Code</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest">Size</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest">Uploaded</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest">Notes</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest">Status</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {releases.map(r => (
                <TableRow key={r.id} data-testid={`release-row-${r.id}`}>
                  <TableCell className="font-bold text-[#002FA7]">v{r.version_name}</TableCell>
                  <TableCell className="font-mono text-xs">#{r.version_code}</TableCell>
                  <TableCell className="text-xs">{formatBytes(r.file_size)}</TableCell>
                  <TableCell className="text-xs text-[#666]">
                    {formatDate(r.uploaded_at)}<br />
                    <span className="text-[10px]">by {r.uploaded_by_name || '—'}</span>
                  </TableCell>
                  <TableCell className="text-xs text-[#666] max-w-[240px] whitespace-pre-wrap">{r.release_notes || '—'}</TableCell>
                  <TableCell>
                    {r.is_current ? (
                      <span className="text-[10px] uppercase tracking-widest font-bold bg-[#00C853]/10 text-[#00C853] px-2 py-1 rounded">Live</span>
                    ) : (
                      <span className="text-[10px] uppercase tracking-widest font-bold bg-[#808080]/10 text-[#808080] px-2 py-1 rounded">Archive</span>
                    )}
                  </TableCell>
                  <TableCell className="text-center">
                    <div className="flex items-center justify-center gap-1">
                      <a
                        href={`${API_BASE}${r.versioned_url}`}
                        className="text-[#002FA7] hover:text-[#001f70] p-1"
                        download
                        title="Download this version"
                        data-testid={`download-${r.id}`}
                      >
                        <CloudArrowUp size={14} weight="bold" className="rotate-180" />
                      </a>
                      {!r.is_current && (
                        <>
                          <button
                            onClick={() => promote(r.id)}
                            className="text-[#FF8C00] hover:text-[#cc7000] p-1"
                            title="Promote to current"
                            data-testid={`promote-${r.id}`}
                          >
                            <ArrowClockwise size={14} weight="bold" />
                          </button>
                          <button
                            onClick={() => remove(r.id)}
                            className="text-[#FF3333] hover:text-[#cc0000] p-1"
                            title="Delete"
                            data-testid={`delete-${r.id}`}
                          >
                            <Trash size={14} weight="bold" />
                          </button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
